// Renderers used by more than one stage: the reading list, the observation stream, finding cards.
import type { Finding, ListKey, ListState, Observation, RunView, StreamItem } from '../shared/types.js';
import { h, quoted, t, tobj } from './dom.js';

export const LIST_KEYS: ListKey[] = ['start', 'ueber-uns', 'leistungen', 'impressum', 'register', 'visual'];

export function pageLabel(key: string): string {
  return tobj<Record<string, string>>('lesen.pages')[key] ?? key;
}

export function pageLong(key: string): string {
  return tobj<Record<string, string>>('befund.pages_long')[key] ?? key;
}

export function renderPagesList(list: Partial<Record<ListKey, ListState>>, pages: RunView['pages'] | null): HTMLUListElement {
  const states = tobj<Record<string, string>>('lesen.page_states');
  return h(
    'ul',
    { class: 'pages', id: 'pages' },
    LIST_KEYS.map((key) => {
      let state: ListState = list[key] ?? 'waiting';
      const page = pages?.find((p) => p.role === key);
      if (page?.missing && (state === 'waiting' || state === 'read')) state = 'missing';
      const fin = ['read', 'matched', 'compared'].includes(state);
      const bad = ['missing', 'failed'].includes(state);
      return h('li', {}, h('span', {}, pageLabel(key)), h('span', { class: `stat${fin ? ' fin' : ''}${bad ? ' bad' : ''}` }, states[state] ?? state));
    })
  );
}

export function streamItemNode(item: StreamItem, visible: boolean): HTMLLIElement {
  const where = tobj<Record<string, string>>('lesen.where')[item.where] ?? item.where;
  const label = item.label ?? (item.anchor ? (tobj<Record<string, string>>('lesen.anchors')[item.anchor] ?? item.anchor) : '');
  const look = item.source !== 'page';
  const li = h(
    'li',
    { class: `${item.miss ? 'miss ' : ''}${look ? 'look ' : ''}${visible ? 'in' : ''}`.trim() },
    h('span', { class: 'where' }, h('b', { html: where }), label),
    h('span', { class: 'txt' }, item.text, item.position ? h('span', { class: 'pos' }, item.position) : null)
  );
  return li;
}

export function renderStream(items: StreamItem[], visible: boolean): HTMLUListElement {
  return h('ul', { class: 'stream', id: 'stream' }, items.map((item) => streamItemNode(item, visible)));
}

export function findObservation(view: RunView, id: string): Observation | undefined {
  return view.observations.find((observation) => observation.id === id);
}

export function quoteNode(observation: Observation, cls = ''): HTMLElement {
  if (observation.absent) return h('div', { class: 'q miss' }, observation.text ?? '', h('span', { class: 'src' }, pageLong(observation.page)));
  return h('div', { class: `q ${cls}`.trim() }, quoted(observation.quote ?? ''), h('span', { class: 'src' }, `${pageLong(observation.page)}${observation.position ? `, ${observation.position}` : ''}`));
}

export function tagClass(finding: Pick<Finding, 'class' | 'priority'>): string {
  if (finding.class === 'Sichtbar und belegt') return 'plus';
  if (finding.priority === 'hoch') return 'high';
  if (finding.priority === 'mittel') return 'mid';
  return 'low';
}

export function renderFinding(view: RunView, finding: Finding, opts: { lead?: boolean; quoteClass?: string } = {}): HTMLElement {
  const classes = tobj<Record<string, string>>('befund.classes');
  const card = h('div', { class: `finding${opts.lead ? ' lead-f' : ''}` });
  card.append(h('span', { class: `tag ${tagClass(finding)}` }, classes[finding.class] ?? finding.class));
  card.append(h('h3', {}, finding.title));
  for (const quote of finding.quotes) {
    const observation = findObservation(view, quote.observation_id);
    if (observation) card.append(quoteNode(observation, opts.quoteClass ?? ''));
  }
  if (finding.absence) {
    const pages = finding.absence.pages.length >= 3 ? t('befund.all_pages') : finding.absence.pages.map(pageLong).join(', ');
    card.append(h('div', { class: 'q miss' }, finding.absence.text, pages ? h('span', { class: 'src' }, pages) : null));
  }
  if (finding.erklaerung) card.append(h('p', {}, finding.erklaerung));
  if (finding.konsequenz) card.append(h('p', { class: 'cons' }, h('b', {}, t('befund.consequence_label')), ' ', finding.konsequenz));
  // The one change the owner can make, free with the finding: what to change, never the wording (2026-10-12).
  if (finding.naechster_schritt) card.append(h('p', { class: 'step' }, h('b', {}, t('befund.step_label')), ' ', finding.naechster_schritt));
  if (finding.class !== 'Sichtbar und belegt' && (finding.ihre_angabe || finding.beobachtung || finding.regel || finding.grenze)) {
    const dl = h('dl', { class: 'know' });
    if (finding.ihre_angabe) dl.append(h('dt', {}, t('befund.know_your_input')), h('dd', {}, finding.ihre_angabe));
    if (finding.beobachtung) dl.append(h('dt', {}, t('befund.know_read')), h('dd', {}, finding.beobachtung));
    if (finding.regel) dl.append(h('dt', {}, t('befund.know_rule')), h('dd', {}, finding.regel));
    if (finding.grenze) dl.append(h('dt', {}, t('befund.know_not_checked')), h('dd', {}, finding.grenze));
    card.append(h('details', { class: 'inline' }, h('summary', {}, t('befund.know_summary')), h('div', { class: 'body' }, dl)));
  }
  return card;
}
